<template>
  <ConfigurationHeader
    :canCreate="data?.policy.heavy_mobile_equipment_enabled"
    :endPointData="endPointData"
    :textData="textData"
    @search="searchWord"
    @show-search="toggleSearch"
    @sort="sortType"
  />

  <div
    :class="{
      'configuration-group': true,
      'configuration-group--padding': !isDesktop(),
      'configuration-group--medium-normal': !showSearch,
      'configuration-group--medium-small': showSearch,
    }"
  >
    <template
      v-for="(heavyMobileEquipmentGroup, index) in searchResults"
      :key="heavyMobileEquipmentGroup?.id"
    >
      <HeaderBar
        v-if="heavyMobileEquipmentGroup && heavyMobileEquipmentGroup.length > 0"
        :modifiers="['narrow', 'no-padding']"
      >
        <p class="configuration-group__info typo-ignore typo-small-title">
          <strong>
            {{ index === 'heavy_mobile_equipment' ? $t('shafts.configuration.heavy_mobile_equipment.nav') : index }}
          </strong>
          <span class="typo-tiny">{{ $t('shafts.configured', { total: heavyMobileEquipmentGroup.length }) }}</span>
        </p>
        <div
          :aria-label="$t('general.sort_by_asc')"
          :class="{
            'configuration-group__icon': true,
            'configuration-group__icon--active': getArrowState(index),
          }"
          @click="updateSort(index)"
        >
          <SortIcon />
        </div>
      </HeaderBar>

      <div
        v-if="heavyMobileEquipmentGroup && heavyMobileEquipmentGroup.length > 0"
        class="configuration-group__list"
      >
        <template
          v-for="heavyMobileEquipment in heavyMobileEquipmentGroup"
          :key="heavyMobileEquipment?.id"
        >
          <ConfigurationItem
            :endpointIdentifier="endPointData.endpointIdentifier"
            :listItem="heavyMobileEquipment"
            :operationType="endPointData.operationType"
            :organisationId="$route.params.organisationId"
            @delete-item="deleteItem($event, index)"
          />
        </template>
      </div>
    </template>
  </div>
  <div class="configuration-new hide-for-medium">
    <router-link
      v-if="data?.policy.heavy_mobile_equipment_enabled"
      class="button"
      :to="{ name: endPointData.newFormRoute }"
    >
      <div class="button__icon button--margin-right">
        <PlusIcon />
      </div>
      {{ textData.new }}
    </router-link>
  </div>
</template>
<script setup>
import { ref, onMounted, watch, reactive } from 'vue';

// Utils
import orderBy from '@/functions/order_by';
import useAjax from '@/functions/use_ajax';
import renameKeys from '@/functions/rename_keys';
import isDesktop from '@/functions/responsive';

// Components
import Searcher from '@/functions/searcher';
import HeaderBar from '@/components/generic/header_bar.vue';
import ConfigurationItem from '@/components/generic/configuration_item.vue';
import ConfigurationHeader from '@/components/generic/configuration_header_template.vue';

// Svg
import PlusIcon from '@/images/plus-16.svg';
import SortIcon from '@/images/sort-arrow-16.svg';

const props = defineProps({
  /** Hash containing endpoints */
  endPointData: { type: Object, required: true, default: null },
  /** Hash of most text in the form */
  textData: { type: Object, required: true, default: null },
  /** The current form data, new or existing */
  data: { type: Object, required: true, default: () => {} },
});

const ajax = useAjax();
const search = ref(null);
const searchResults = ref({});
const initialData = reactive({});
const loading = ref(null);
const sortBy = ref(null);
const showSearch = ref(false);
const sortStates = reactive([]);
const searcher = reactive([]);
let searchKeys = ['name'];
const byName = orderBy('name', (x) => x.toLowerCase());

onMounted(() => {
  loadHme();
});

/**
 * Watches for changes on the sort by dropdown
 */
watch(sortBy, () => {
  if (!searchResults.value) return null;
  // sort group
  switch (sortBy.value) {
    case 'alphabetical':
      searchResults.value = Object.fromEntries(
        Object.entries(searchResults.value).sort()
      );
      break;
    case 'alphabetical_reverse':
      searchResults.value = Object.fromEntries(
        Object.entries(searchResults.value).sort().reverse()
      );
      break;
  }
});

watch(() => initialData, buildSearcher);

function buildSearcher(items = initialData) {
  if (!items) return;
  Object.keys(items).forEach((group) => {
    searcher[group] = new Searcher(items[group], { keys: searchKeys });
  });
}

async function loadHme() {
  if (props.data?.id) {
    loading.value = true;
    let { ok, data } = await ajax(props.endPointData.hmeGroupsEndpoint);

    if (ok) {
      /**
       * TODO: remove when heavy_mobile_equipment has been changed to assets on the database.
       * Right now the view says Assets but when filtering it uses heavy_mobile_equipment key
       * and places the item in the wrong order
       */
      data = renameKeys(data);
      searchResults.value = data;
      Object.assign(initialData, data);
      Object.keys(data).forEach((group) => {
        sortStates.push({
          label: group,
          is_ascending: null,
        });
        updateSort(group, 'alphabetical');
      });
      loading.value = false;
      buildSearcher();
    }
  }
}

/**
 * Watch and set search
 */
watch(search, (value) => {
  Object.keys(searchResults.value).forEach((group) => {
    if (value) {
      searchResults.value[group] = searcher[group]?.search(value);
    } else {
      searchResults.value[group] = initialData[group];
    }
    updateSort(group, false);
  });
});

function deleteItem(event, group) {
  searchResults.value[group] = searchResults.value[group].filter((item) => item.id !== event);
}

function updateSort(groupKey, toggle = true) {
  const foundIndex = sortStates.find((key) => key.label === groupKey);
  toggle && (foundIndex.is_ascending = !foundIndex.is_ascending);

  const sortFn = foundIndex.is_ascending ? byName.asc : byName.desc;
  searchResults.value[foundIndex.label].sort(sortFn);
}

function getArrowState(groupKey) {
  return !sortStates?.find((arrow) => arrow.label === groupKey)?.is_ascending;
}

function sortType(data) {
  sortBy.value = data;
}

function searchWord(data) {
  search.value = data;
}

function toggleSearch(data) {
  showSearch.value = data;
}
</script>
